import React from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import {
  HeatMaterialWithColor,
  MixDisplayMode,
  ProcessedBasketSource,
  mixValueModeFor,
  mixViewFor,
} from "../types";
import { HeatMixPlot } from "./HeatMixPlot";
import { unitForMixView } from "./mixViewUnit";
import { useTranslation } from "react-i18next";

interface HeatBasketSourceCardProps {
  source: ProcessedBasketSource;
  isFirst: boolean;
  displayMode: MixDisplayMode;
}

export const HeatBasketSourceCard: React.FC<HeatBasketSourceCardProps> = ({
  source,
  isFirst,
  displayMode,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!source.hasBasket) {
    return (
      <Stack direction="column" alignItems="center" width={244} gap={1}>
        <Typography variant="h5" textAlign="center">
          {t(source.sourceName)}
        </Typography>
        <Box
          sx={{
            width: "100%",
            minHeight: 120,
            bgcolor: theme.palette.grey[100],
            border: `2px dashed ${theme.palette.grey[400]}`,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2" color="text.disabled">
            {t("noBasket")}
          </Typography>
        </Box>
      </Stack>
    );
  }

  const mixView = mixViewFor(displayMode);
  const mixValueMode = mixValueModeFor(displayMode);
  const isPercent = mixValueMode === "mass_fraction";
  const unit = unitForMixView(mixView);
  const total = source.materials.reduce((sum, m) => sum + m.value, 0);
  const referenceTotal = source.materials.reduce(
    (sum, m) => sum + m.value - (m.diff ?? 0),
    0
  );
  const materials: HeatMaterialWithColor[] = source.materials.map((m) => {
    const value = isPercent && total > 0 ? (100 * m.value) / total : m.value;
    const refValue = m.value - (m.diff ?? 0);
    const pctDiff =
      m.diff !== null && total > 0 && referenceTotal > 0
        ? (100 * m.value) / total - (100 * refValue) / referenceTotal
        : 0;
    return {
      name: m.name,
      value,
      unit,
      diffFromPreviousStage: isPercent ? pctDiff : (m.diff ?? 0),
      ignored: !m.isPresent,
      highlighted: false,
      color: m.color,
    };
  });

  return (
    <Stack direction="column" alignItems="center" width={244} gap={1}>
      <Typography variant="h5" textAlign="center">
        {t(source.sourceName)}
      </Typography>
      <HeatMixPlot
        materials={materials}
        isFirst={isFirst}
        mixValueMode={mixValueMode}
      />
    </Stack>
  );
};
